import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import styled from 'styled-components';

// Components
import { DateInput, Dropdown, DropdownOptions, Input } from '@mlb/play-ui';
import { noop, CountryOfResidence } from '@gif/shared-global';
import {
  CountryOfResidenceSection,
  InformationSection,
  TermsContainer,
  ProvinceDropdown,
  AdditionalInformationContainer,
  RequiredText,
  StyledButton,
} from './AdditionalInformation.styles';
import { FormErrorNotification } from './FormErrorNotification';

// Utils
import { isAddInfoFormValid } from './isAddInfoFormValid';
import { getUserArcadeDobMonthDayYear } from '../../../utils';
import { translate } from '../../../configs';

// Types
import { FormValueErrors, FormValues } from './AdditionalInformation.types';

// Hooks
import { useGetApprovedCountriesQuery } from '../../../api';
import { useArcadeUser } from '../../../hooks';

const AddInfoDateInput = styled(DateInput)`
  margin: 1rem 0;
`;

const useArcadeUserProps = {
  options: {
    fixedCacheKey: 'shared-refresh-profile',
  },
};
const nicknameLabel = translate('nickname');
const countryLabel = translate('countryOfResidence');
const provinceLabel = translate('province');
const tosHtml = {
  __html: translate('updateAccountToS', {
    tos: 'https://www.mlb.com/official-information/terms-of-use',
    privacy: 'https://www.mlb.com/official-information/privacy-policy',
    tosES: 'https://www.mlb.com/es/official-information/terms-of-use',
    privacyES: 'https://www.mlb.com/es/official-information/privacy-policy',
  }),
};

export interface AdditionalInformationProps {
  onFormSubmit?: (args: FormValues) => void;
}

export const AdditionalInformation = (props: AdditionalInformationProps) => {
  const { onFormSubmit = noop } = props;
  const { data: approvedCountriesData, isLoading: isCountriesLoading } =
    useGetApprovedCountriesQuery();
  const { arcadeUser, isLoading: isArcadeUserLoading } =
    useArcadeUser(useArcadeUserProps);
  const [nicknameValue, setNicknameValue] = useState('');
  const [monthValue, setMonthValue] = useState('');
  const [dayValue, setDayValue] = useState('');
  const [yearValue, setYearValue] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('');
  const [isCanada, setIsCanada] = useState(false);
  const [formHasErrors, setFormHasErrors] = useState(false);
  const [formErrors, setFormErrors] = useState<FormValueErrors>(
    {} as FormValueErrors
  );
  const countriesResidence = useMemo(
    () => approvedCountriesData?.result,
    [approvedCountriesData]
  );
  const countries = useMemo(
    () => countriesResidence?.countries,
    [countriesResidence]
  );
  const provinces = useMemo(
    () => countriesResidence?.provinces,
    [countriesResidence]
  );
  const countriesDropdownOptions = useMemo(
    () =>
      countries?.map((country) => ({
        value: country.countryCode,
        label: country.name,
      })) as DropdownOptions[],
    [countries]
  );
  const provincesDropdownOptions = useMemo(
    () =>
      provinces?.map((province) => ({
        value: province.provinceCode,
        label: province.name,
      })) as DropdownOptions[],
    [provinces]
  );
  const isLoading = isArcadeUserLoading || isCountriesLoading;

  const handleUserNameOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFormErrors((formErrors) => ({
        ...formErrors,
        nickName: '',
      }));
      setFormHasErrors(false);
      setNicknameValue(event.target.value);
    },
    []
  );
  const handleMonthOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFormErrors((formErrors) => ({
        ...formErrors,
        birthMonth: '',
      }));
      setFormHasErrors(false);
      setMonthValue(event.target.value);
    },
    []
  );
  const handleDayOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFormErrors((formErrors) => ({
        ...formErrors,
        birthDay: '',
      }));
      setFormHasErrors(false);
      setDayValue(event.target.value);
    },
    []
  );
  const handleYearOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFormErrors((formErrors) => ({
        ...formErrors,
        birthYear: '',
      }));
      setFormHasErrors(false);
      setYearValue(event.target.value);
    },
    []
  );
  const handleCountrySelection = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setFormErrors((formErrors) => ({
        ...formErrors,
        country: '',
      }));
      setFormHasErrors(false);
      const countryVal = event.target.value;
      const isCanada = countryVal.toLowerCase() === 'ca';
      setSelectedCountry(countryVal);
      setIsCanada(isCanada);
      if (!isCanada) {
        setSelectedProvince('');
      }
    },
    []
  );
  const handleProvinceSelection = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setFormErrors((formErrors) => ({
        ...formErrors,
        province: '',
      }));
      setFormHasErrors(false);
      setSelectedProvince(event.target.value);
    },
    []
  );
  const handleUpdateAccountSubmission = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (isLoading) {
        return;
      }
      const formData = new FormData(event.currentTarget);
      const data = Object.fromEntries(formData.entries()); // provides array of obj containing form {key: value} from name props
      const submittedData = {
        nickName: data['nickname'] as string,
        birthDay: data['day'] as string,
        birthMonth: data['month'] as string,
        birthYear: data['year'] as string,
        country: {
          country: countries?.find((c) => c.countryCode === data['country']),
          province:
            provinces?.find((p) => p.provinceCode === data['province']) || null,
        } as CountryOfResidence,
      };
      const { errors, isValid } = isAddInfoFormValid(submittedData);
      setFormErrors(errors);
      setFormHasErrors(!isValid);
      if (!isValid) {
        return;
      }
      onFormSubmit(submittedData);
    },
    [onFormSubmit, countries, provinces, isLoading]
  );
  useEffect(() => {
    if (isLoading) {
      return;
    }
    const arcadeUserCountryInfo = arcadeUser?.countryInfo;
    const arcadeUserCountry = arcadeUserCountryInfo?.country;
    const arcadeUserCountryCode = arcadeUserCountry?.countryCode;
    setSelectedCountry(arcadeUserCountryCode || '');
    if (arcadeUserCountryCode?.toLowerCase() === 'ca') {
      setIsCanada(true);
      setSelectedProvince(arcadeUserCountryInfo?.province?.provinceCode || '');
    }
    setNicknameValue(arcadeUser?.nickname || '');
    const arcadeUserBirthday = arcadeUser?.dob;
    if (arcadeUserBirthday) {
      const arcadeUserBirthdayInfo =
        getUserArcadeDobMonthDayYear(arcadeUserBirthday);
      setDayValue(arcadeUserBirthdayInfo.birthDay);
      setMonthValue(arcadeUserBirthdayInfo.birthMonth);
      setYearValue(arcadeUserBirthdayInfo.birthYear);
    }
  }, [arcadeUser, isLoading]);

  if (isLoading) {
    return <>Loading...</>;
  }

  return (
    <AdditionalInformationContainer>
      <RequiredText>{translate('allFieldsRequired')}</RequiredText>
      <form onSubmit={handleUpdateAccountSubmission}>
        <Input
          id="nickname"
          value={nicknameValue}
          onChange={handleUserNameOnChange}
          label={nicknameLabel}
          name="nickname"
          placeholder={`${nicknameLabel} *`}
          hasError={!!formErrors.nickName}
        />
        <AddInfoDateInput
          legend={`${translate('dateOfBirth')} *`}
          monthLabel={translate('month')}
          monthPlaceholder={translate('MM')}
          monthValue={monthValue}
          onMonthChange={handleMonthOnChange}
          monthHasError={!!formErrors.birthMonth}
          dayLabel={translate('day')}
          dayPlaceholder={translate('DD')}
          dayValue={dayValue}
          onDayChange={handleDayOnChange}
          dayHasError={!!formErrors.birthDay}
          yearLabel={translate('year')}
          yearPlaceholder={translate('YYYY')}
          yearValue={yearValue}
          onYearChange={handleYearOnChange}
          yearHasError={!!formErrors.birthYear}
        />
        <CountryOfResidenceSection aria-label={countryLabel}>
          <Dropdown
            placeholder={`${countryLabel} *`}
            id="country"
            ariaLabel={translate('country')}
            name="country"
            options={countriesDropdownOptions}
            value={selectedCountry}
            onChange={handleCountrySelection}
            message={translate('countryNotListed')}
            hasError={!!formErrors.country}
          />
          {isCanada && (
            <ProvinceDropdown
              id="province"
              placeholder={`${provinceLabel} *`}
              ariaLabel={provinceLabel}
              name="province"
              options={provincesDropdownOptions}
              value={selectedProvince}
              onChange={handleProvinceSelection}
              hasError={!!formErrors.province}
            />
          )}
        </CountryOfResidenceSection>
        <InformationSection>
          <StyledButton type="submit">
            {translate('updateInformation')}
          </StyledButton>
          <TermsContainer dangerouslySetInnerHTML={tosHtml} />
        </InformationSection>
      </form>
      {formHasErrors && <FormErrorNotification formErrors={formErrors} />}
    </AdditionalInformationContainer>
  );
};

export default AdditionalInformation;
